import React, { HTMLAttributes, MouseEvent, ReactNode, useState } from 'react';
import { CSSTransition } from 'react-transition-group';
import { LoadingSpinner } from '@noths/polaris-client-ribbons-design-system';

import * as styles from '../styles/LoadButton.styles';

export const SPINNER_CLASSNAME = 'pagination__load-more-spinner';
export const SPINNER_ANIMATION_DURATION_IN_MS = 300;

export interface LoadButtonProps extends HTMLAttributes<HTMLAnchorElement> {
  linkUrl: string;
  isLoading: boolean;
  children: ReactNode;
  testId: string;
  onClick: (e: MouseEvent<HTMLAnchorElement>) => void;
}

const loadMoreButtonStyles = styles.loadMoreButton(
  SPINNER_CLASSNAME,
  SPINNER_ANIMATION_DURATION_IN_MS,
);

export const LoadButton = ({
  children,
  isLoading,
  linkUrl,
  testId,
  onClick,
  ...props
}: LoadButtonProps) => {
  const [isSpinnerAnimating, setIsSpinnerAnimating] = useState(false);

  /**
   * Although this renders a link, because we are styling it as a button and in an enhanced
   * experience it behaves like a button, we are setting the role to "button" to allow people
   * who use voice recognition assistive technology to correctly identify this element.
   * */
  return (
    <a href={linkUrl} css={loadMoreButtonStyles} role="button" onClick={onClick} {...props}>
      {!isLoading && !isSpinnerAnimating && children}

      <CSSTransition
        in={isLoading}
        timeout={SPINNER_ANIMATION_DURATION_IN_MS}
        onEnter={() => setIsSpinnerAnimating(true)}
        onExited={() => setIsSpinnerAnimating(false)}
        classNames={SPINNER_CLASSNAME}
        unmountOnExit
      >
        <div css={styles.loadingSpinner} data-testid={testId}>
          <LoadingSpinner />
        </div>
      </CSSTransition>
    </a>
  );
};
